import { Controller } from "stimulus"

# Controls the subscribe/subscribed button pair in the newsletter viewer
# header. Every state is always rendered; this controller decides which one is
# visible based on whether a subscription exists and, if so, whether that
# subscription has email notifications turned on.
export default class extends Controller
  @targets: ['subscribe', 'subscribed', 'notificationsOn', 'notificationsOff',
             'bellIcon', 'bellSlashIcon']

  @values =
    # ID of the current user's NewsletterSubscription. Blank/0 means the
    # user is not subscribed.
    subscriptionId: Number
    # Whether the existing subscription has email notifications enabled.
    receiveNotifications: Boolean
    # Whether the visitor is logged in. Unauthenticated visitors get the
    # authentication modal instead of a subscribe request.
    currentUserAuthenticated: Boolean
    # Paths the authentication modal sends unauthenticated visitors to.
    loginPath: String
    signupPath: String
    # Title of the newsletter, used in the authentication modal's copy.
    newsletterName: String
    # Newsletter::Configuration the subscription is created against.
    newsletterConfigurationId: Number
    # Index path for User::NewsletterSubscriptionsController. Member paths are
    # built from it and the subscription's ID.
    subscriptionsPath: String

  connect: ->
    @render()

  subscriptionIdValueChanged: ->
    @render()

  receiveNotificationsValueChanged: ->
    @render()

  # ACTIONS

  subscribe: (event) ->
    event.preventDefault()

    return @showAuthenticateModal() unless @currentUserAuthenticatedValue

    @request('POST', @subscriptionsPathValue,
      newsletter_configuration_id: @newsletterConfigurationIdValue)

  unsubscribe: (event) ->
    event.preventDefault()

    return unless @subscribed()

    @confirm "Are you sure you want to unsubscribe from #{ @newsletterNameValue }? You won't get email notifications about new editions, or see it or its updates in your profile.", =>
      @request('DELETE', @memberPath())

  toggleNotifications: (event) ->
    event.preventDefault()

    return unless @subscribed()

    receiveNotifications = !@receiveNotificationsValue

    perform = =>
      @request('PATCH', @memberPath(), receive_email_notifications: receiveNotifications)

    # Only turning notifications off is gated — turning them back on is not
    # something anyone needs talking out of.
    return perform() if receiveNotifications

    @confirm "Are you sure you want to turn off email notifications for #{ @newsletterNameValue }? You'll stay subscribed and still see it and its updates in your profile. You just won't be emailed when a new edition publishes.", perform

  # PRIVATE

  memberPath: ->
    "#{ @subscriptionsPathValue }/#{ @subscriptionIdValue }"

  # Opens the app's standard remodal confirmation dialog, running the callback
  # only if the person confirms. Mirrors
  # App.Views.Friendship.BaseView#_displayUnfriendConfirmationModal.
  confirm: (confirmationText, callback) ->
    modal = $(App.Templates["templates/modals/text_confirmation_modal"]
      isPlain: true
      confirmationText: confirmationText
    ).remodal()

    modal.$modal.on 'confirmation', callback
    modal.open()

  # Sends the request and applies the returned subscription state to this
  # controller's values, which re-renders the buttons.
  request: (method, path, body) ->
    options =
      method: method
      mode: 'same-origin'
      credentials: 'same-origin'
      headers:
        'X-CSRF-Token': document.querySelector("[name='csrf-token']").content
        'Content-Type': 'application/json'
        'Accept': 'application/json'

    options.body = JSON.stringify(body) if body

    fetch(path, options).then((response) =>
      throw new Error(response.status) unless response.ok
      response.json()
    ).then((subscription) =>
      # An unsubscribed record is archived rather than deleted, so the server
      # still returns its ID. Zero it out so the buttons read as unsubscribed.
      @subscriptionIdValue = if subscription.subscribed then subscription.id else 0
      @receiveNotificationsValue = subscription.receive_email_notifications
    ).catch((error) => @render())

  subscribed: ->
    @subscriptionIdValue > 0

  render: ->
    @toggle(@subscribeTarget, !@subscribed())
    @toggle(@subscribedTarget, @subscribed())

    if @hasNotificationsOnTarget and @hasNotificationsOffTarget
      # The "turn off" action shows while notifications are on, and vice versa.
      @toggle(@notificationsOffTarget, @receiveNotificationsValue)
      @toggle(@notificationsOnTarget, !@receiveNotificationsValue)

    if @hasBellIconTarget and @hasBellSlashIconTarget
      # The icon, unlike the actions above, reflects the current state.
      @toggle(@bellIconTarget, @receiveNotificationsValue)
      @toggle(@bellSlashIconTarget, !@receiveNotificationsValue)

  toggle: (element, visible) ->
    element.hidden = !visible

  showAuthenticateModal: ->
    # Render the Authentication modals content from the template using the login and signup path that are provided to the controller.
    @authenticationModal or= new App.Views.AuthenticationModalView
      model: new Backbone.Model
        loginPath: @loginPathValue
        signupPath: @signupPathValue
        newsletterAuthExplanationFor: @newsletterNameValue
        postLogInTip: "Once logged in, you will automatically become a subscriber of #{ @newsletterNameValue }."
        postSignUpTip: "After creating your account, you will automatically become a subscriber of #{ @newsletterNameValue }."

    @authenticationModal.render()